/**
 * Shared JSX for the Geography admin tab sections (WPS-222): the section
 * chrome (heading + count line + toolbar with search / facets / actions) and
 * the two cell renderers every table uses. Helpers live in geography-utils.ts.
 */

import type { ReactNode } from 'react'

import { SearchInput } from '@/shared/components/ui/search-input'
import {
  ProductFilterMenu,
  type ProductFilterFacet,
  type ProductFilterValue,
} from '@/shared/components/ui/product-filter-menu'

export function GeographySectionShell({
  title,
  description,
  countLine,
  search,
  onSearchChange,
  searchPlaceholder,
  facets,
  filterValues,
  onFilterValuesChange,
  actions,
  children,
  testId,
}: {
  title: string
  description?: string
  countLine?: string
  search?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  facets?: ProductFilterFacet[]
  filterValues?: ProductFilterValue
  onFilterValuesChange?: (value: ProductFilterValue) => void
  actions?: ReactNode
  children: ReactNode
  testId?: string
}) {
  return (
    <section className="flex flex-col gap-5" data-testid={testId}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="clara-heading">{title}</h2>
          {description ? (
            <p className="mt-1 max-w-[720px] text-[13px] leading-relaxed text-[var(--ink-3)]">
              {description}
            </p>
          ) : null}
          {countLine ? (
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">{countLine}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {facets && filterValues && onFilterValuesChange ? (
            <ProductFilterMenu
              facets={facets}
              value={filterValues}
              onChange={onFilterValuesChange}
              triggerClassName="h-[34px] rounded-[6px] border-[0.5px] border-[var(--rule-strong)] px-3 text-[13px]"
            />
          ) : null}
          {onSearchChange ? (
            <SearchInput
              placeholder={searchPlaceholder}
              value={search ?? ''}
              onChange={onSearchChange}
              className="cv-data-table-body h-[34px] w-[220px] rounded-[6px] border-[0.5px] border-solid border-[var(--rule-strong)]"
            />
          ) : null}
          {actions}
        </div>
      </div>
      {children}
    </section>
  )
}

/** Muted mono code cell used for zone / postal / SKU codes across the tab. */
export function CodeCell({ value }: { value: string | null | undefined }) {
  return (
    <span className="font-mono text-[11px] text-[var(--ink-3)]">{value || '-'}</span>
  )
}

export function TextCell({
  value,
  muted = false,
}: {
  value: ReactNode
  muted?: boolean
}) {
  return (
    <span className={muted ? 'text-[13px] text-[var(--ink-2)]' : 'text-[13px] text-[var(--ink)]'}>
      {value ?? '-'}
    </span>
  )
}
